import { useMemo } from 'react'

import { Table, Text } from '@domain-group/suit-theme'
import type { Column } from '@domain-group/suit-theme/dist/components/Table'

import useSelector from '@hooks/useSelector'

import type { EntitiesState } from '@redux/entities'

import { OrderBundleItemModel } from '@models/orderBundleItem'

import useCurrentUser from '@hooks/useCurrentUser'

import OrderBundleProductActions from './OrderBundleProductActions'

interface OrderBundleProductsTableProps {
  filteredOrderBundleItems: OrderBundleItemModel[],
}

const buildColumns = (
  currentProviderId: number,
  isAdmin: boolean,
  providers: EntitiesState['providers'],
  providerPriceLists: EntitiesState['providerPriceLists'],
  publications: EntitiesState['publications'],
) => [
  {
    title: 'Provider',
    dataKey: 'provider_id',
    render: (providerId: number) => {
      const title = currentProviderId === providerId ? providers[providerId]?.title || 'N/A' : 'Other Provider'

      return (
        <Text variant="ellipsis" minWidth="100px" style={{ whiteSpace: 'nowrap' }}>
          {currentProviderId ? title : providers[providerId]?.title || 'N/A'}
        </Text>
      )
    },
  },
  {
    title: 'Price List',
    dataKey: 'provider_price_list_id',
    render: (providerPriceListId: number) => (
      <Text variant="ellipsis" minWidth="100px" style={{ whiteSpace: 'nowrap' }}>
        {providerPriceLists[providerPriceListId]?.title || 'N/A'}
      </Text>
    ),
  },
  {
    title: 'Publication',
    dataKey: 'publication_id',
    render: (publicationId: number) => (
      <Text variant="ellipsis" minWidth="100px" maxWidth="200px">
        {publications[publicationId]?.title || 'N/A'}
      </Text>
    ),
  },
  {
    title: 'Item',
    dataKey: 'title',
    render: (title: string) => (
      <Text variant="ellipsis" minWidth="100px" maxWidth="250px">
        {title || 'N/A'}
      </Text>
    ),
  },
  {
    title: 'Product Name',
    dataKey: 'product_name',
    render: (productName: string) => (
      <Text variant="ellipsis" minWidth="100px" maxWidth="200px">
        {productName || 'N/A'}
      </Text>
    ),
  },
  {
    title: 'Product Code',
    dataKey: 'product_code',
    render: (productCode: number) => productCode || 'N/A',
  },
  {
    title: 'Price',
    dataKey: 'price_override',
    cellProps: {
      width: 100,
    },
    render: (priceOverride: string) => priceOverride || 'N/A',
  },
  {
    title: 'Sort',
    dataKey: 'sort',
    cellProps: {
      width: 100,
    },
    render: (sort: number) => sort || 'Default',
  },
  {
    title: 'Actions',
    dataKey: 'id',
    cellProps: {
      textAlign: 'end',
    },
    render: (id: number) => (
      <OrderBundleProductActions
        currentProviderId={currentProviderId}
        isAdmin={isAdmin}
        orderBundleItem={{ id }}
      />
    ),
  },
]

const OrderBundleProductsTable = (props: OrderBundleProductsTableProps) => {
  const { filteredOrderBundleItems } = props

  const { providerPriceLists, providers, publications } = useSelector(reduxState => reduxState.entities)

  const { isAdmin, provider, isProviderUser } = useCurrentUser()

  const currentProviderId = isProviderUser ? provider?.id : null

  const memoColumns = useMemo(
    // @ts-expect-error TS2345
    (): Column[] => buildColumns(currentProviderId, isAdmin, providers, providerPriceLists, publications),
    [filteredOrderBundleItems.length],
  )

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 300, overflowY: 'auto' }}
      columns={memoColumns}
      data={filteredOrderBundleItems}
      stickyHeader
    />
  )
}

export default OrderBundleProductsTable
